import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PendingAuthStep } from "@/components/AuthSteps";
import { useAuth } from "@/context/AuthContext";
import { LOGOUT_REASON_KEY } from "@/components/SessionCutoff";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GoogleSignInButton, OrDivider } from "@/components/GoogleSignInButton";

export default function Login() {
  const { login, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoutReason] = useState(() => {
    const r = sessionStorage.getItem(LOGOUT_REASON_KEY);
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return r;
  });
  const [pending, setPending] = useState(location.state?.pending || null);

  useEffect(() => {
    if (location.state?.pending) navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.pending) {
      setPending(res.pending);
    } else if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      toast.error(res.error);
    }
  };

  const onStepDone = (data) => {
    if (data.user) {
      setSession(data);
      toast.success("Welcome back");
      navigate("/");
    } else {
      setPending(data);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid-backdrop grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 bg-white border-r border-slate-200">
        <div className="flex items-center gap-3">
          <img src="/kms-logo.png" alt="KMS logo" className="w-12 h-12 rounded-full object-contain" />
          <div>
            <div className="font-semibold text-lg tracking-tight leading-none">KMS-Obligo</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Admin suite for corporates &amp; family offices</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl font-semibold tracking-tight leading-[1.05]">
            Your entire back office,<br />
            <span className="text-amber-600">one workspace.</span>
          </h1>
          <p className="mt-6 text-sm text-slate-600 leading-relaxed">
            Tasks, timesheets, obligations and client portals for corporates &amp; family offices.
          </p>
        </div>
        <div className="text-xs text-slate-500 mono">Admin Suite · v1.0</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        {pending ? (
          <PendingAuthStep key={pending.challenge_token || pending.email} pending={pending} onDone={onStepDone}
            onBack={() => { setPending(null); setPassword(""); navigate("/login", { replace: true, state: null }); }} />
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 rounded-md p-8 animate-fade-up">
          <h2 className="text-2xl font-semibold tracking-tight mb-1" data-testid="login-heading">Sign in</h2>
          <p className="text-sm text-slate-500 mb-6">Access your admin workspace.</p>
          {logoutReason && <div data-testid="login-cutoff-notice" className="mb-5 text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-md p-3 leading-relaxed">{logoutReason}</div>}
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="login-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 mono" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-slate-600 hover:text-blue-700 underline underline-offset-4">Forgot password?</Link>
              </div>
              <Input data-testid="login-password" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 mono" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <OrDivider />
          <GoogleSignInButton testid="login-google-btn" />
          <p className="text-sm text-slate-500 mt-6 text-center">
            No account?{" "}
            <Link data-testid="link-to-register" to="/register" className="font-medium text-zinc-950 underline underline-offset-4">Create one</Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
